import { Box, Divider, IconButton, Typography } from '@mui/material'
import React from 'react'
import { commify } from 'ethers/lib/utils'
import Link from 'next/link'
import { FlexCol } from '@/components/common/Columns'
import { Row, RowBetweenCenter, RowCenterY } from '@/components/common/Rows'
import TokenLogo from '@/components/common/TokenLogo'
import { Ticker } from '@/constants/token'
import { shouldUseValutContract } from '@/utils'
import { CHAIN_INFO, COLD_WALLET_ADDRESS, HOT_WALLET_ADDRESS } from '@/constants'
import BitcoinSvg from '@/assets/imgs/bitcoin.svg'
import useCrossChainVolumn from '@/hooks/useCrossChainVolumn'
import { ExistingCrossChainAmount } from '@/components/bridge/Dashboard/ExistingCrossChainAmount'
import { getOklinkPageUrl, shortenAddress, shortenBtcAddress } from '@/utils/address'
import SwapLinkButton from '@/components/bridge/Dashboard/SwapLinkButton'
import { VAULT_ADDRESS } from '@/constants/contract/contractAddress'

const AttestationCard = ({ ticker, price }: { ticker: Ticker; price?: number }) => {
  const { totalCrossChainVolumeMap } = useCrossChainVolumn()

  const volumn = totalCrossChainVolumeMap?.[ticker.name]
  const formattedVolumn = volumn ? commify(volumn.toFixed(2)) : null

  const formattedPrice = price && typeof volumn === 'number' ? `$ ${commify((volumn * price).toFixed(2))}` : null

  return (
    <Box sx={{ border: '1px solid rgba(191, 255, 11, 0.20)' }}>
      <RowCenterY sx={{ bgcolor: '#1C1F0C', borderBottom: '1px solid rgba(191, 255, 11, 0.20)', px: 20, py: 12 }}>
        <TokenLogo src={ticker?.logoSrcSet.brc} size={56} alt="" />

        <Typography sx={{ fontSize: '20px', fontWeight: 700, ml: 12 }}>{ticker.name}</Typography>
        {typeof price === 'number' && (
          <Typography sx={{ fontSize: '20px', fontWeight: 700, ml: 'auto' }}>
            $ {commify(Number(price).toFixed(8))}
          </Typography>
        )}
      </RowCenterY>

      <FlexCol sx={{ px: 20, py: 16, gap: 16 }}>
        <RowBetweenCenter>
          <Typography sx={{ fontSize: '14px', fontWeight: 400, opacity: 0.7 }}>Origin</Typography>
          {shouldUseValutContract(ticker.name) ? (
            <RowCenterY gap={12}>
              {ticker.supportChains.map((chainId) => (
                <Link
                  key={chainId}
                  href={`${CHAIN_INFO[chainId].blockExplorers.default.url}/token/${ticker.erc20Tokens[chainId].address}`}
                >
                  <IconButton size="small">
                    <TokenLogo src={CHAIN_INFO[chainId].logoSrc} size={24} alt={CHAIN_INFO[chainId].name} />
                  </IconButton>
                </Link>
              ))}
            </RowCenterY>
          ) : (
            <RowCenterY sx={{ p: 5 }}>
              <TokenLogo src={BitcoinSvg} size={24} alt="bitcoin" />
            </RowCenterY>
          )}
        </RowBetweenCenter>

        <RowBetweenCenter>
          <Typography sx={{ fontSize: '14px', fontWeight: 400, opacity: 0.7 }}>Target</Typography>

          {shouldUseValutContract(ticker.name) ? (
            <RowCenterY sx={{ p: 5 }}>
              <TokenLogo src={BitcoinSvg} size={24} alt="bitcoin" />
            </RowCenterY>
          ) : (
            <RowCenterY gap={4}>
              {ticker.supportChains.map((chainId) => (
                <Link
                  key={chainId}
                  target="_blank"
                  href={`${CHAIN_INFO[chainId].blockExplorers.default.url}/token/${ticker.erc20Tokens[chainId].address}`}
                >
                  <IconButton size="small">
                    <TokenLogo src={CHAIN_INFO[chainId].logoSrc} size={24} alt={CHAIN_INFO[chainId].name} />
                  </IconButton>
                </Link>
              ))}
            </RowCenterY>
          )}
        </RowBetweenCenter>

        <FlexCol gap={12}>
          <Typography sx={{ fontSize: '14px', fontWeight: 400, opacity: 0.7 }}>Cross Chain Volume</Typography>
          <Row sx={{ alignItems: 'baseline', columnGap: 8 }}>
            <Typography>{formattedVolumn ? `${formattedVolumn} ${ticker.name}` : '-'}</Typography>

            {formattedPrice && formattedVolumn && (
              <Typography
                component="span"
                sx={{ color: 'rgba(255, 255, 255, 0.6)', whiteSpace: 'nowrap', fontSize: '12px' }}
              >
                {formattedPrice}
              </Typography>
            )}
          </Row>
        </FlexCol>

        <Divider sx={{ borderColor: '#344205' }} />

        <FlexCol gap={12}>
          <FlexCol gap={12}>
            <Typography sx={{ fontSize: '14px', fontWeight: 400, opacity: 0.7 }}>
              Existing cross-chain amount
            </Typography>
            <ExistingCrossChainAmount tickerName={ticker.name} />
          </FlexCol>

          <FlexCol gap={12}>
            <Typography sx={{ fontSize: '14px', fontWeight: 400, opacity: 0.7 }}>attestation Address</Typography>

            {shouldUseValutContract(ticker.name) ? (
              ticker.supportChains.map((chainId) => (
                <RowBetweenCenter key={chainId}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 400 }}>
                    {CHAIN_INFO[chainId].abbreviation} contract address
                  </Typography>
                  <Link
                    href={`${CHAIN_INFO[chainId].blockExplorers.default.url}/address/${VAULT_ADDRESS[chainId]}`}
                    target="_blank"
                  >
                    <Typography sx={{ fontSize: '14px', fontWeight: 400, textDecorationLine: 'underline' }}>
                      {shortenAddress(VAULT_ADDRESS[chainId])}
                    </Typography>
                  </Link>
                </RowBetweenCenter>
              ))
            ) : (
              <>
                <RowBetweenCenter>
                  <Typography sx={{ fontSize: '14px', fontWeight: 400 }}>Hot wallet</Typography>
                  <Link href={getOklinkPageUrl(HOT_WALLET_ADDRESS, 'address')} target="_blank">
                    <Typography sx={{ fontSize: '14px', fontWeight: 400, textDecorationLine: 'underline' }}>
                      {shortenBtcAddress(HOT_WALLET_ADDRESS)}
                    </Typography>
                  </Link>
                </RowBetweenCenter>

                <RowBetweenCenter>
                  <Typography sx={{ fontSize: '14px', fontWeight: 400 }}>Cold wallet</Typography>
                  <Link href={getOklinkPageUrl(COLD_WALLET_ADDRESS, 'address')} target="_blank">
                    <Typography sx={{ fontSize: '14px', fontWeight: 400, textDecorationLine: 'underline' }}>
                      {shortenBtcAddress(COLD_WALLET_ADDRESS)}
                    </Typography>
                  </Link>
                </RowBetweenCenter>
              </>
            )}

            {ticker?.swapInfo ? (
              <RowBetweenCenter>
                <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#BFFF0B' }}>Project SWAP</Typography>
                <RowCenterY gap={4}>
                  {ticker.swapInfo.map((swap) => (
                    <SwapLinkButton key={swap.url} swap={swap.name} href={swap.url} />
                  ))}
                </RowCenterY>
              </RowBetweenCenter>
            ) : null}
          </FlexCol>
        </FlexCol>
      </FlexCol>
    </Box>
  )
}

export default AttestationCard
